import { Badge, Card, Disclosure, buttonVariants, cn } from "@safeplate/ui";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Check, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";

import { LoadingState } from "../components/states.js";
import { useShell } from "../shell/shell-context.js";
import type { ChecklistStep } from "./setup-checklist-model.js";
import { useSetupChecklist } from "./use-setup-checklist.js";

function StepLink({
  children,
  className,
  step,
}: {
  children: ReactNode;
  className?: string;
  step: ChecklistStep;
}) {
  return step.target.search ? (
    <Link className={className} search={step.target.search} to="/setup">
      {children}
    </Link>
  ) : (
    <Link className={className} to={step.target.to}>
      {children}
    </Link>
  );
}

function StepRow({ index, step }: { index: number; step: ChecklistStep }) {
  return (
    <li>
      <StepLink
        className="flex min-h-14 items-center gap-3 focus-inset px-4 py-3 transition hover:bg-stone-50"
        step={step}
      >
        <span
          aria-hidden="true"
          className={cn(
            "grid size-7 shrink-0 place-items-center rounded-full text-xs font-bold",
            step.done
              ? "bg-emerald-800 text-white"
              : "border border-stone-300 text-stone-500",
          )}
        >
          {step.done ? (
            <Check className="size-4" />
          ) : step.optional ? null : (
            index + 1
          )}
        </span>
        <span
          className={cn(
            "min-w-0 flex-1 text-sm font-semibold",
            step.done ? "text-stone-500" : "text-stone-950",
          )}
        >
          {step.label}
          <span className="sr-only">{step.done ? ", done" : ""}</span>
        </span>
        {step.detail ? <Badge tone="neutral">{step.detail}</Badge> : null}
        <ChevronRight
          aria-hidden="true"
          className="size-4 shrink-0 text-stone-400"
        />
      </StepLink>
    </li>
  );
}

function ProgressBar({
  done,
  percent,
  total,
}: {
  done: number;
  percent: number;
  total: number;
}) {
  return (
    <div className="flex items-center gap-3">
      <div
        aria-label={`${done} of ${total} done`}
        aria-valuemax={total}
        aria-valuemin={0}
        aria-valuenow={done}
        className="h-1.5 max-w-60 flex-1 overflow-hidden rounded-full bg-stone-200"
        role="progressbar"
      >
        <div
          className="h-full rounded-full bg-emerald-800 transition-all"
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className="text-sm font-semibold whitespace-nowrap text-stone-600">
        {done} of {total}
      </span>
    </div>
  );
}

// The site's form for booking a setup visit.
export const SETUP_VISIT_URL = "https://safeplate.nz/contact/#setup";

function SetUpWithUs() {
  return (
    <a
      className={cn(buttonVariants({ variant: "ghost" }), "w-full sm:w-auto")}
      href={SETUP_VISIT_URL}
      rel="noreferrer"
      target="_blank"
    >
      Set up with us
    </a>
  );
}

export function SetupChecklist({
  variant = "page",
}: {
  variant?: "embedded" | "page";
}) {
  const shell = useShell();
  const checklist = useSetupChecklist({ full: variant === "page" });

  if (shell.activeRole === "staff") return null;
  if (variant === "embedded" && (!checklist.loaded || checklist.complete)) {
    return null;
  }
  if (!checklist.loaded) return <LoadingState label="Loading setup" />;

  const { done, next, percent, steps, total } = checklist;
  const required = steps.filter((step) => !step.optional);
  const later = steps.filter((step) => step.optional);

  if (variant === "embedded") {
    return (
      <Card className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
        <div className="min-w-0 flex-1 space-y-2">
          <h2 className="text-base font-semibold text-stone-950">
            <Link
              className="hover:underline"
              search={{ section: "checklist" }}
              to="/setup"
            >
              Finish setting up
            </Link>
          </h2>
          <ProgressBar done={done} percent={percent} total={total} />
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <SetUpWithUs />
          {next ? (
            <StepLink
              className={cn(buttonVariants(), "w-full sm:w-auto")}
              step={next}
            >
              {next.label} <ArrowRight aria-hidden="true" className="size-4" />
            </StepLink>
          ) : null}
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      <Card className="overflow-hidden">
        <div className="flex flex-col gap-4 border-b border-stone-200 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
          <div className="min-w-0 flex-1 space-y-2">
            <h2 className="text-base font-semibold text-stone-950">
              {checklist.complete ? "Kitchen ready" : "Checklist"}
            </h2>
            <ProgressBar done={done} percent={percent} total={total} />
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <SetUpWithUs />
            {next ? (
              <StepLink
                className={cn(buttonVariants(), "w-full sm:w-auto")}
                step={next}
              >
                {next.label}{" "}
                <ArrowRight aria-hidden="true" className="size-4" />
              </StepLink>
            ) : null}
          </div>
        </div>
        <ol className="divide-y divide-stone-200">
          {required.map((step, index) => (
            <StepRow index={index} key={step.key} step={step} />
          ))}
        </ol>
      </Card>
      {later.length > 0 ? (
        <Disclosure
          summary={`Before your verification · ${later.filter((step) => step.done).length} of ${later.length}`}
        >
          <Card className="overflow-hidden">
            <ol className="divide-y divide-stone-200">
              {later.map((step, index) => (
                <StepRow
                  index={required.length + index}
                  key={step.key}
                  step={step}
                />
              ))}
            </ol>
          </Card>
        </Disclosure>
      ) : null}
    </div>
  );
}
